import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ActivateAccount from './pages/ActivateAccount';
import Dashboard from './pages/Dashboard';
import Asignaciones from './pages/Asignaciones';
import Multas from './pages/Multas';
import AdminDashboard from './pages/AdminDashboard';
import AdminEmpresas from './pages/AdminEmpresas';
import AdminUsuarios from './pages/AdminUsuarios';
import MultasPendientes from './pages/MultasPendientes';
import EditarMulta from './pages/EditarMulta';
import Profile from './pages/Profile';
import AdminCertificados from './pages/AdminCertificados';
import CentroAyuda from './pages/CentroAyuda';
import Recursos from './pages/Recursos';
import AdminRecursos from './pages/AdminRecursos';
import SubirMulta from './pages/SubirMulta';
import LandingPage from './pages/LandingPage';
import PropuestaComercial from './pages/PropuestaComercial';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AdminTrials from './pages/AdminTrials';
import AdminMultas from './pages/AdminMultas';
import AdminMultasNoComunicables from './pages/AdminMultasNoComunicables';
import AdminMultaDetalle from './pages/AdminMultaDetalle';
import AdminRecargos from './pages/AdminRecargos';
import EmpresaCertificados from './pages/EmpresaCertificados';
import Subarrendatarios from './pages/Subarrendatarios';
import AdminLicenciatarios from './pages/AdminLicenciatarios';
import AdminBoeScans from './pages/AdminBoeScans';
import AdminFacturacion from './pages/AdminFacturacion';
import AsignacionesSubarrendatarios from './pages/AsignacionesSubarrendatarios';
import FirmaRemota from './pages/FirmaRemota';

/**
 * Componente de carga mientras se verifica la sesión
 */
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Cargando...</p>
      </div>
    </div>
  );
}

/**
 * Ruta protegida que requiere autenticación
 * Usa el contexto de autenticación (httpOnly cookies)
 */
function ProtectedRoute({ children, adminOnly = false }) {
  const { usuario, loading, estaAutenticado, esAdmin } = useAuth();

  // Mostrar loading mientras se verifica la sesión
  if (loading) {
    return <LoadingScreen />;
  }

  // Redirigir a login si no está autenticado
  if (!estaAutenticado()) {
    return <Navigate to="/login" replace />;
  }

  // Redirigir a dashboard si no es admin pero la ruta requiere admin
  if (adminOnly && !esAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * Ruta pública que redirige si ya está autenticado
 */
function PublicRoute({ children }) {
  const { loading, estaAutenticado, esAdmin } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  // Si ya está autenticado, redirigir al dashboard apropiado
  if (estaAutenticado()) {
    return <Navigate to={esAdmin() ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return children;
}

/**
 * Rutas de la aplicación
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page Pública */}
      <Route path="/" element={<LandingPage />} />

      {/* Propuesta Comercial (pública, imprimible) */}
      <Route path="/propuesta" element={<PropuestaComercial />} />

      {/* GDPR - Políticas (públicas) */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terminos" element={<TermsOfService />} />

      {/* Auth - Públicas con redirección si autenticado */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/activate/:token" element={<ActivateAccount />} />

      {/* Firma Remota - Pública (sin autenticación) */}
      <Route path="/firmar/:token" element={<FirmaRemota />} />

      {/* Rutas Admin */}
      <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/empresas" element={<ProtectedRoute adminOnly><AdminEmpresas /></ProtectedRoute>} />
      <Route path="/admin/usuarios" element={<ProtectedRoute adminOnly><AdminUsuarios /></ProtectedRoute>} />
      <Route path="/admin/multas" element={<ProtectedRoute adminOnly><AdminMultas /></ProtectedRoute>} />
      <Route path="/admin/multas/pendientes" element={<ProtectedRoute adminOnly><MultasPendientes /></ProtectedRoute>} />
      {/* OJO AL ORDEN: tiene que ir ANTES de /admin/multas/:id, o React Router
          interpretaria "no-comunicables" como un id y abriria el detalle. */}
      <Route path="/admin/multas/no-comunicables" element={<ProtectedRoute adminOnly><AdminMultasNoComunicables /></ProtectedRoute>} />
      <Route path="/admin/recargos" element={<ProtectedRoute adminOnly><AdminRecargos /></ProtectedRoute>} />
      <Route path="/admin/multas/:id/editar" element={<ProtectedRoute adminOnly><EditarMulta /></ProtectedRoute>} />
      <Route path="/admin/multas/:id" element={<ProtectedRoute adminOnly><AdminMultaDetalle /></ProtectedRoute>} />
      <Route path="/admin/recursos" element={<ProtectedRoute adminOnly><AdminRecursos /></ProtectedRoute>} />
      <Route path="/admin/certificados" element={<ProtectedRoute adminOnly><AdminCertificados /></ProtectedRoute>} />
      <Route path="/admin/trials" element={<ProtectedRoute adminOnly><AdminTrials /></ProtectedRoute>} />
      <Route path="/admin/subarrendatarios" element={<ProtectedRoute adminOnly><Subarrendatarios /></ProtectedRoute>} />
      <Route path="/admin/licenciatarios" element={<ProtectedRoute adminOnly><AdminLicenciatarios /></ProtectedRoute>} />
      <Route path="/admin/asignaciones-subarrendatarios" element={<ProtectedRoute adminOnly><AsignacionesSubarrendatarios /></ProtectedRoute>} />
      <Route path="/admin/boe-scans" element={<ProtectedRoute adminOnly><AdminBoeScans /></ProtectedRoute>} />
      <Route path="/admin/facturacion" element={<ProtectedRoute adminOnly><AdminFacturacion /></ProtectedRoute>} />

      {/* Rutas Usuario */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/asignaciones" element={<ProtectedRoute><Asignaciones /></ProtectedRoute>} />
      <Route path="/multas" element={<ProtectedRoute><Multas /></ProtectedRoute>} />
      <Route path="/subir-multa" element={<ProtectedRoute><SubirMulta /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/recursos" element={<ProtectedRoute><Recursos /></ProtectedRoute>} />
      <Route path="/ayuda" element={<ProtectedRoute><CentroAyuda /></ProtectedRoute>} />
      <Route path="/empresa/certificados" element={<ProtectedRoute><EmpresaCertificados /></ProtectedRoute>} />

      {/* Redirect legacy login path */}
      <Route path="/login-old" element={<Navigate to="/login" />} />

      {/* 404 - Redirige a landing */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

/**
 * App principal con AuthProvider
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
